import {
  CashManager,
  CashOperation,
  CashOperationResponse,
} from './gestorEfectivo.interface';

const WIN = typeof window !== 'undefined' ? (window as any) : null;

/**
 * CashKeeper cash manager implementation.
 * Calls native functions exposed under `window.external`.
 * Returns follow the documented format: `OK:...` | `ERR:...`.
 */
export class CashKeeperGestorEfectivo implements CashManager {
  private connected = false;
  private host: string | null = null;
  private managerName: string | null = null;

  async connect(managerName: string, host: string): Promise<boolean> {
    console.log('[CashKeeper] connect() called');
    if (!host || host.trim() === '') {
      console.warn('[CashKeeper] Empty host.');
      return false;
    }
    this.managerName = managerName;
    this.host = "192.168.249.10";

    if (WIN?.external && typeof WIN.external.inicializarGestorEfectivo === 'function') {
      try {
        const res = WIN.external.inicializarGestorEfectivo(managerName, this.host);
        const ok = typeof res === 'string' && res.toUpperCase().startsWith('OK:CONECTADO');
        if (ok) {
          this.connected = true;
          console.log('[CashKeeper] Connected:', this.managerName, this.host);
        } else {
          const msg = typeof res === 'string' ? res : String(res);
          console.warn('[CashKeeper] Error conexión:', msg);
        }
        return ok;
      } catch (e) {
        console.warn('[CashKeeper] Excepción al conectar:', e);
        return false;
      }
    }

    console.warn(
      '[CashKeeper] window.external not available (cannot connect from browser).'
    );
    this.connected = false;
    return false;
  }

  async disconnect(): Promise<void> {
    if (!this.connected) {
      return;
    }
    console.log('[CashKeeper] Disconnecting:', this.managerName, this.host);
    if (WIN?.external && typeof WIN.external.desconectarGestorEfectivo === 'function') {
      try {
        const res = WIN.external.desconectarGestorEfectivo();
        if (typeof res === 'string' && !res.toUpperCase().startsWith('OK:DESCONECTADO')) {
          console.warn('[CashKeeper] Error al desconectar:', res);
        } else {
          console.log('[CashKeeper] Desconectado (external):', res);
        }
      } catch (e) {
        console.warn('[CashKeeper] Excepción al desconectar:', e);
      }
    }
    this.connected = false;
    this.host = null;
    this.managerName = null;
  }

  isConnected(): boolean {
    return this.connected;
  }

  async processCashOperation(
    operation: CashOperation,
    amountCents: number
  ): Promise<CashOperationResponse> {
    const amount = Math.round(amountCents);
    console.log('[CashKeeper] processCashOperation', { operation, amountCents: amount });
    if (WIN?.external && typeof WIN.external.ventaGestorEfectivo === 'function') {
      try {
        const res = WIN.external.ventaGestorEfectivo(operation, amount, 0, 0);
        console.log('[CashKeeper] ventaGestorEfectivo respuesta external:', res);
        return this.parseCashOperationResponse(res, operation, amountCents);
      } catch (e) {
        console.warn('[CashKeeper] Excepción ventaGestorEfectivo:', e);
        return { ok: false, error: e instanceof Error ? e.message : String(e) };
      }
    }
    console.warn(
      '[CashKeeper] ventaGestorEfectivo sin external: operación no disponible'
    );
    return { ok: false, error: 'ERR:NO CONECTADO' };
  }

  private parseCashOperationResponse(
    res: unknown,
    operation: CashOperation,
    amountCents: number
  ): CashOperationResponse {
    const s = typeof res === 'string' ? res : String(res);
    if (s.toUpperCase().startsWith('ERR:')) {
      return { ok: false, error: s.trim() || 'ERR:GENERICO' };
    }
    if (!s.toUpperCase().startsWith('OK:')) {
      return { ok: false, error: s || 'Respuesta no reconocida' };
    }
    const introduced = this.extractNumber(s, 'Introducido');
    const returned = this.extractNumber(s, 'Devuelto');
    if (introduced === null && returned === null) {
      return {
        ok: true,
        introducedCents: amountCents,
        returnedCents: 0,
      };
    }
    return {
      ok: true,
      introducedCents: introduced ?? 0,
      returnedCents: returned ?? 0,
    };
  }

  private extractNumber(texto: string, clave: string): number | null {
    const regex = new RegExp(`${clave}:\\s*(\\d+)`, 'i');
    const m = texto.match(regex);
    if (!m) {
      return null;
    }
    const n = parseInt(m[1], 10);
    return isNaN(n) ? null : n;
  }
}
